import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { type IPeerParticipant, type IPeerThreadSummary } from '../interface/peer-chat';
import { isPeerKey } from '../lib/chat-thread-ref';
import { peerChatEnabled } from '../lib/peer-chat-flags';
import { counterpartOf } from '../lib/peer-inbox';
import { fetchPeerThreads } from '../lib/support-api';
import { threadKeyOf } from '../lib/support-signalr';
import { type SupportReach } from './useChatThread';
import { type ISupportSession } from './useSupportSession';

/** Poll cadence. The inbox is the badge's source while no peer engine is mounted. */
const POLL_CLOSED_MS = 60_000;
const POLL_OPEN_MS = 20_000;
/** A server without the feature is not going to grow it in the next minute. */
const POLL_UNAVAILABLE_MS = 600_000;

export interface IPeerInbox {
	threads: IPeerThreadSummary[];
	/** How many the server holds, so a capped page can say "showing N of M". */
	totalCount: number;
	reach: SupportReach;
	loading: boolean;
	refresh: () => void;
	counterpartOf: (summary: IPeerThreadSummary) => IPeerParticipant;
	/** Unread by counterpart core user id, for per-row badges on the PU list and coverage panel. */
	unreadByCounterpart: ReadonlyMap<number, number>;
	summaryFor: (counterpartCoreUserId: number) => IPeerThreadSummary | undefined;
}

export interface IPeerInboxOptions {
	session: ISupportSession;
	/** The Messages inbox panel is on screen — polls faster. */
	isInboxOpen: boolean;
}

const reachFromStatus = (status?: number): SupportReach => {
	if (status === 404 || status === 405) return 'unavailable';
	if (status === 403) return 'forbidden';
	if (status === 401) return 'signedOut';
	return 'failed';
};

const statusOf = (error: unknown): number | undefined => (error as { response?: { status?: number } })?.response?.status;

/**
 * The caller's peer threads for the current election, kept fresh.
 *
 * <p>This is also the capability probe: the first `GET peer/threads` says whether the server
 * has agent ↔ agent chat at all (404 → `unavailable`, and the poll backs off to ten minutes).
 * A 403 means the account cannot list — stop; the surfaces explain. A network failure keeps
 * the normal cadence, because it will come back.</p>
 *
 * <p>Mounted once in the layout. It listens to the shared hub for `SupportUnreadChanged` with a
 * peer key (or none — older servers send it bare) and for `SupportThreadUpdated`, and refreshes
 * rather than trusting the payload: the list, not one row, is what the badge sums.</p>
 */
export const usePeerInbox = (electionId: number | undefined, { session, isInboxOpen }: IPeerInboxOptions): IPeerInbox => {
	const { me, live, liveStatus } = session;
	const enabled = !!me && !!electionId && electionId > 0 && peerChatEnabled();

	const [threads, setThreads] = useState<IPeerThreadSummary[]>([]);
	const [totalCount, setTotalCount] = useState(0);
	const [reach, setReach] = useState<SupportReach>('unknown');
	const [loading, setLoading] = useState(false);

	const inFlightRef = useRef<Promise<void> | null>(null);
	const reachRef = useRef<SupportReach>('unknown');
	reachRef.current = reach;
	/**
	 * Earliest time the next automatic list may run. Set ten minutes out after a 404: a server
	 * without the feature is not going to grow it before then, and the normal cadence would
	 * otherwise spend a request every minute confirming the same answer. User intent
	 * (`refresh()` from a panel) ignores it.
	 */
	const notBeforeRef = useRef(0);

	const list = useCallback(
		(force: boolean) => {
			if (!enabled || !electionId) return;
			if (inFlightRef.current) return;
			if (!force && Date.now() < notBeforeRef.current) return;

			const flight = (async () => {
				setLoading(true);
				try {
					const page = await fetchPeerThreads(electionId);
					setThreads(page.threads);
					setTotalCount(page.totalCount);
					setReach('ok');
					notBeforeRef.current = 0;
				} catch (error) {
					const status = statusOf(error);
					const next = reachFromStatus(status);
					setReach(next);
					notBeforeRef.current = next === 'unavailable' ? Date.now() + POLL_UNAVAILABLE_MS : 0;
					// console.error so console-buffer.ts ships it with "Report a problem". Status only.
					console.error('[peer-chat] could not list conversations', { status: status ?? 'network' });
				} finally {
					setLoading(false);
					inFlightRef.current = null;
				}
			})();
			inFlightRef.current = flight;
		},
		[enabled, electionId]
	);

	const listRef = useRef(list);
	listRef.current = list;

	/** User intent: a panel asking for the list now. Clears the post-404 hold. */
	const refresh = useCallback(() => listRef.current(true), []);

	/** A new election is a new list: drop the old rows and the post-404 hold with them. */
	useEffect(() => {
		setThreads([]);
		setTotalCount(0);
		setReach('unknown');
		notBeforeRef.current = 0;
	}, [electionId]);

	/** Coming back online is fresh evidence; the hold was about the server, not the network. */
	useEffect(() => {
		if (!enabled) return;
		const onOnline = () => {
			notBeforeRef.current = 0;
			if (reachRef.current !== 'forbidden') listRef.current(false);
		};
		window.addEventListener('online', onOnline);
		return () => window.removeEventListener('online', onOnline);
	}, [enabled]);

	/** Poll at a cadence that depends on whether anyone is looking; the post-404 hold is enforced inside `list`. */
	useEffect(() => {
		if (!enabled) return;

		listRef.current(false);

		const interval = isInboxOpen ? POLL_OPEN_MS : POLL_CLOSED_MS;
		const timer = window.setInterval(() => {
			if (reachRef.current !== 'forbidden') listRef.current(false);
		}, interval);

		const onVisible = () => {
			if (document.visibilityState === 'visible' && reachRef.current !== 'forbidden') listRef.current(false);
		};
		document.addEventListener('visibilitychange', onVisible);

		return () => {
			window.clearInterval(timer);
			document.removeEventListener('visibilitychange', onVisible);
		};
	}, [enabled, isInboxOpen]);

	/** Re-list when the hub says a peer thread changed; the connect event covers what was missed while down. */
	useEffect(() => {
		if (!enabled || !live) return;

		const onSignal = (payload: unknown) => {
			const key = threadKeyOf(payload);
			if (key !== undefined && !isPeerKey(key)) return;
			if (reachRef.current === 'forbidden') return;
			listRef.current(false);
		};

		const unsubscribes = [live.on('SupportUnreadChanged', onSignal), live.on('SupportThreadUpdated', onSignal)];
		return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
	}, [enabled, live]);

	useEffect(() => {
		if (!enabled || liveStatus !== 'connected') return;
		if (reachRef.current === 'forbidden') return;
		listRef.current(false);
	}, [enabled, liveStatus]);

	const unreadByCounterpart = useMemo(() => {
		const map = new Map<number, number>();
		for (const summary of threads) {
			const other = counterpartOf(summary, me);
			if (!other.coreUserId) continue;
			map.set(other.coreUserId, Math.max(0, summary.unreadForMe ?? 0));
		}
		return map;
	}, [me, threads]);

	const byCounterpart = useMemo(() => {
		const map = new Map<number, IPeerThreadSummary>();
		for (const summary of threads) {
			const other = counterpartOf(summary, me);
			if (other.coreUserId) map.set(other.coreUserId, summary);
		}
		return map;
	}, [me, threads]);

	return useMemo(
		() => ({
			threads,
			totalCount,
			reach,
			loading,
			refresh,
			counterpartOf: (summary: IPeerThreadSummary) => counterpartOf(summary, me),
			unreadByCounterpart,
			summaryFor: (counterpartCoreUserId: number) => byCounterpart.get(counterpartCoreUserId),
		}),
		[threads, totalCount, reach, loading, refresh, me, unreadByCounterpart, byCounterpart]
	);
};
